import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Flame, LogOut, Plus, Edit, Trash2, Save, X, ShoppingBag, Ticket, Package, Sparkles } from 'lucide-react';
import axios from 'axios';
import AIImageGenerator from '../components/AIImageGenerator';
import ImageUploader from '../components/ImageUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboardNew = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('products'); // products, orders, coupons, ai-generator
  const [orderStatusFilter, setOrderStatusFilter] = useState('all'); // all, pending, accepted, rejected
  const [selectedOrder, setSelectedOrder] = useState(null); // for detailed view
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    type: 'Baked',
    price: '',
    originalPrice: '',
    stock: '100',
    badge: '',
    images: '',
    nutrition: { protein: '', sugar: '', fat: '' }
  });
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_value: '',
    expiry_date: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/admin/login');
      return;
    }
    if (activeTab === 'products') fetchProducts();
    else if (activeTab === 'orders') fetchOrders();
    else if (activeTab === 'coupons') fetchCoupons();
  }, [user, navigate, activeTab]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/products`);
      setProducts(data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching products:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const getFilteredOrders = () => {
    if (orderStatusFilter === 'all') return orders;
    if (orderStatusFilter === 'pending') {
      // Show all new unprocessed orders
      return orders.filter(order => ['pending', 'created', 'paid'].includes(order.status));
    }
    return orders.filter(order => order.status === orderStatusFilter);
  };

  const getOrderStatusColor = (status) => {
    switch(status) {
      case 'pending':
      case 'created':
      case 'paid':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'hold': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'accepted': return 'bg-green-100 text-green-800 border-green-300';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-300';
      case 'shipped': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'delivered': return 'bg-purple-100 text-purple-800 border-purple-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getPaymentMethod = (order) => {
    if (order.razorpay_payment_id) {
      return 'Razorpay (Online)';
    }
    return 'Cash on Delivery';
  };

  const formatOrderDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOrderItemsCount = (order) => {
    return order.items?.reduce((sum, item) => sum + (item.quantity || item.qty || 1), 0) || 0;
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/orders`, { withCredentials: true });
      setOrders(data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching orders:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/coupons`, { withCredentials: true });
      setCoupons(data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching coupons:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const startEdit = (product) => {
    setEditingId(product.id);
    setEditForm({
      name: product.name,
      description: product.description,
      type: product.type,
      price: product.price,
      originalPrice: product.originalPrice || '',
      stock: product.stock || 0,
      badge: product.badge || '',
      images: product.images || [],
      nutrition: {
        protein: product.nutrition?.protein || '',
        sugar: product.nutrition?.sugar || '',
        fat: product.nutrition?.fat || ''
      }
    });
  };

  const saveEdit = async (productId) => {
    try {
      const updateData = {
        ...editForm,
        price: parseInt(editForm.price),
        originalPrice: editForm.originalPrice ? parseInt(editForm.originalPrice) : null,
        stock: parseInt(editForm.stock),
        images: Array.isArray(editForm.images) ? editForm.images : []
      };
      
      await axios.put(
        `${API}/products/${productId}`,
        updateData,
        { withCredentials: true }
      );
      
      // Refresh products list to show updated data
      await fetchProducts();
      setEditingId(null);
      
      // Show success message
      alert('✅ Product updated successfully! Changes are now live on your website.');
    } catch (error) {
      alert('❌ Failed to update product: ' + (error.response?.data?.detail || error.message));
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('⚠️ Are you sure you want to delete this product? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/products/${productId}`, { withCredentials: true });
      await fetchProducts();
      alert('✅ Product deleted successfully!');
    } catch (error) {
      alert('❌ Failed to delete product: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    try {
      const productData = {
        ...newProduct,
        price: parseInt(newProduct.price),
        originalPrice: newProduct.originalPrice ? parseInt(newProduct.originalPrice) : null,
        stock: parseInt(newProduct.stock),
        images: newProduct.images.split(',').map(url => url.trim()).filter(Boolean),
        nutrition: {
          protein: newProduct.nutrition.protein || '0g',
          sugar: newProduct.nutrition.sugar || '0g',
          fat: newProduct.nutrition.fat || '0g'
        }
      };

      await axios.post(`${API}/products`, productData, { withCredentials: true });
      setShowAddForm(false);
      setNewProduct({
        name: '',
        description: '',
        type: 'Baked',
        price: '',
        originalPrice: '',
        stock: '100',
        badge: '',
        images: '',
        nutrition: { protein: '', sugar: '', fat: '' }
      });
      await fetchProducts();
      alert('✅ Product added successfully! It is now visible on your website.');
    } catch (error) {
      alert('❌ Failed to add product: ' + (error.response?.data?.detail || error.message));
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      const response = await axios.put(
        `${API}/orders/${orderId}/status?status=${status}`,
        {},
        { withCredentials: true }
      );
      
      const message = response.data.message || `Order ${status} successfully`;
      const shiprocket = response.data.shiprocket;
      
      if (status === 'accepted' && shiprocket) {
        alert(`${message}\n\nShipment Details:\n- AWB: ${shiprocket.awb_code || 'Pending'}\n- Courier: ${shiprocket.courier_name || 'TBD'}`);
      } else {
        alert(message);
      }
      
      await fetchOrders();
    } catch (error) {
      alert('Failed to update order: ' + (error.response?.data?.detail || error.message));
    }
  };

  const viewLabel = (labelUrl) => {
    if (labelUrl) {
      window.open(labelUrl, '_blank');
    } else {
      alert('Label not yet available');
    }
  };

  const trackShipment = async (orderId) => {
    try {
      const { data } = await axios.get(`${API}/orders/${orderId}/tracking`, { withCredentials: true });
      const trackingInfo = `Tracking Info:\nAWB: ${data.awb_code}\nCourier: ${data.courier_name}`;
      alert(trackingInfo);
      if (process.env.NODE_ENV === 'development') {
        console.log('Full tracking data:', data);
      }
    } catch (error) {
      alert('Failed to fetch tracking: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    try {
      const couponData = {
        ...newCoupon,
        discount_value: parseInt(newCoupon.discount_value),
        min_order_value: parseInt(newCoupon.min_order_value) || 0,
        expiry_date: new Date(newCoupon.expiry_date).toISOString()
      };

      await axios.post(`${API}/coupons`, couponData, { withCredentials: true });
      setShowCouponForm(false);
      setNewCoupon({
        code: '',
        discount_type: 'percentage',
        discount_value: '',
        min_order_value: '',
        expiry_date: ''
      });
      await fetchCoupons();
    } catch (error) {
      alert('Failed to create coupon: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex items-center justify-center">
        <p className="text-xl text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-orange-100 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Flame className="w-8 h-8 text-orange-500" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-sm text-gray-600">Welcome, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/')} variant="outline">
              View Site
            </Button>
            <Button onClick={handleLogout} variant="outline" className="text-red-600">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab('products')}
              className={`py-4 px-2 border-b-2 font-medium transition-colors ${
                activeTab === 'products'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Package className="w-4 h-4 inline mr-2" />
              Products
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className={`py-4 px-2 border-b-2 font-medium transition-colors ${
                activeTab === 'orders'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <ShoppingBag className="w-4 h-4 inline mr-2" />
              Orders
            </button>
            <button
              onClick={() => setActiveTab('coupons')}
              className={`py-4 px-2 border-b-2 font-medium transition-colors ${
                activeTab === 'coupons'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Ticket className="w-4 h-4 inline mr-2" />
              Coupons
            </button>
            <button
              onClick={() => setActiveTab('ai-generator')}
              className={`py-4 px-2 border-b-2 font-medium transition-colors ${
                activeTab === 'ai-generator'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Sparkles className="w-4 h-4 inline mr-2" />
              AI Image Generator
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Products Tab */}
        {activeTab === 'products' && (
          <>
            {/* Add Product Button */}
            <div className="mb-6 flex justify-between items-center">
              <h2 className="text-3xl font-bold text-gray-900">Product Management</h2>
              <Button
                onClick={() => setShowAddForm(!showAddForm)}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add New Product
              </Button>
            </div>

        {/* Add Product Form */}
        {showAddForm && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Add New Product</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddProduct} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Product Name *</Label>
                    <Input
                      value={newProduct.name}
                      onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Type *</Label>
                    <Input
                      value={newProduct.type}
                      onChange={(e) => setNewProduct({ ...newProduct, type: e.target.value })}
                      placeholder="e.g., Baked, Roasted"
                      required
                    />
                  </div>
                  <div>
                    <Label>Price (₹) *</Label>
                    <Input
                      type="number"
                      value={newProduct.price}
                      onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Original Price (₹)</Label>
                    <Input
                      type="number"
                      value={newProduct.originalPrice}
                      onChange={(e) => setNewProduct({ ...newProduct, originalPrice: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Stock *</Label>
                    <Input
                      type="number"
                      value={newProduct.stock}
                      onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Badge</Label>
                    <Input
                      value={newProduct.badge}
                      onChange={(e) => setNewProduct({ ...newProduct, badge: e.target.value })}
                      placeholder="e.g., 🔥 Bestseller"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Description *</Label>
                  <Input
                    value={newProduct.description}
                    onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label>Product Images *</Label>
                  <p className="text-sm text-gray-500 mb-3">
                    Upload product images from your desktop (max 5 images)
                  </p>
                  <ImageUploader
                    maxImages={5}
                    existingImages={newProduct.images ? newProduct.images.split(',').map(url => url.trim()).filter(Boolean) : []}
                    onImagesUploaded={(urls) => {
                      setNewProduct({ ...newProduct, images: urls.join(', ') });
                    }}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Protein (per 100g)</Label>
                    <Input
                      value={newProduct.nutrition.protein}
                      onChange={(e) => setNewProduct({ ...newProduct, nutrition: { ...newProduct.nutrition, protein: e.target.value } })}
                      placeholder="17g"
                    />
                  </div>
                  <div>
                    <Label>Sugar (per 100g)</Label>
                    <Input
                      value={newProduct.nutrition.sugar}
                      onChange={(e) => setNewProduct({ ...newProduct, nutrition: { ...newProduct.nutrition, sugar: e.target.value } })}
                      placeholder="4g"
                    />
                  </div>
                  <div>
                    <Label>Fat (per 100g)</Label>
                    <Input
                      value={newProduct.nutrition.fat}
                      onChange={(e) => setNewProduct({ ...newProduct, nutrition: { ...newProduct.nutrition, fat: e.target.value } })}
                      placeholder="4g"
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button type="submit" className="bg-orange-500 hover:bg-orange-600">
                    Add Product
                  </Button>
                  <Button type="button" onClick={() => setShowAddForm(false)} variant="outline">
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Products List */}
        <div className="space-y-4">
          {products.map((product) => (
            <Card key={product.id}>
              <CardContent className="p-6">
                {editingId === product.id ? (
                  /* FULL EDIT MODE */
                  <div className="space-y-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-bold text-gray-900">Edit Product</h3>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => saveEdit(product.id)}
                          size="sm"
                          className="bg-green-500 hover:bg-green-600"
                        >
                          <Save className="w-4 h-4 mr-1" />
                          Save Changes
                        </Button>
                        <Button
                          onClick={() => setEditingId(null)}
                          size="sm"
                          variant="outline"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Product Name *</Label>
                        <Input
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label>Type *</Label>
                        <Input
                          value={editForm.type}
                          onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                          placeholder="e.g., Baked, Roasted"
                          required
                        />
                      </div>
                      <div>
                        <Label>Price (₹) *</Label>
                        <Input
                          type="number"
                          value={editForm.price}
                          onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label>Original Price (₹)</Label>
                        <Input
                          type="number"
                          value={editForm.originalPrice}
                          onChange={(e) => setEditForm({ ...editForm, originalPrice: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Stock *</Label>
                        <Input
                          type="number"
                          value={editForm.stock}
                          onChange={(e) => setEditForm({ ...editForm, stock: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label>Badge</Label>
                        <Input
                          value={editForm.badge}
                          onChange={(e) => setEditForm({ ...editForm, badge: e.target.value })}
                          placeholder="e.g., 🔥 Bestseller"
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Description *</Label>
                      <Input
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        required
                      />
                    </div>

                    <div>
                      <Label>Product Images *</Label>
                      <p className="text-sm text-gray-500 mb-3">
                        Manage product images - upload new or remove existing images
                      </p>
                      <ImageUploader
                        maxImages={5}
                        existingImages={editForm.images}
                        onImagesUploaded={(urls) => {
                          setEditForm({ ...editForm, images: urls });
                        }}
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Protein (per 100g)</Label>
                        <Input
                          value={editForm.nutrition.protein}
                          onChange={(e) => setEditForm({ 
                            ...editForm, 
                            nutrition: { ...editForm.nutrition, protein: e.target.value } 
                          })}
                          placeholder="17g"
                        />
                      </div>
                      <div>
                        <Label>Sugar (per 100g)</Label>
                        <Input
                          value={editForm.nutrition.sugar}
                          onChange={(e) => setEditForm({ 
                            ...editForm, 
                            nutrition: { ...editForm.nutrition, sugar: e.target.value } 
                          })}
                          placeholder="4g"
                        />
                      </div>
                      <div>
                        <Label>Fat (per 100g)</Label>
                        <Input
                          value={editForm.nutrition.fat}
                          onChange={(e) => setEditForm({ 
                            ...editForm, 
                            nutrition: { ...editForm.nutrition, fat: e.target.value } 
                          })}
                          placeholder="4g"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  /* VIEW MODE */
                  <div className="flex items-start gap-6">
                    {/* Product Image */}
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-24 h-24 object-cover rounded-lg"
                    />

                    {/* Product Info */}
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{product.name}</h3>
                      <p className="text-gray-600 text-sm mb-2">{product.description}</p>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm text-gray-500">Type:</span>
                        <span className="text-sm font-semibold">{product.type}</span>
                        {product.badge && (
                          <span className="text-sm bg-orange-100 text-orange-600 px-2 py-1 rounded">
                            {product.badge}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">Images:</span>
                        <span className="text-sm font-semibold">{product.images?.length || 0} image(s)</span>
                      </div>
                    </div>

                    {/* Price & Actions */}
                    <div className="w-64">
                      <div className="space-y-2">
                        <div>
                          {product.originalPrice && (
                            <div className="text-gray-400 line-through text-sm">₹{product.originalPrice}</div>
                          )}
                          <div className="text-2xl font-bold text-orange-500">₹{product.price}</div>
                        </div>
                        <div className="text-sm text-gray-600">
                          Stock: <span className="font-semibold">{product.stock || 0}</span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => startEdit(product)}
                            size="sm"
                            className="bg-orange-500 hover:bg-orange-600 flex-1"
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            onClick={() => deleteProduct(product.id)}
                            size="sm"
                            variant="outline"
                            className="text-red-600"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
          </>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <>
            <div className="mb-6 flex justify-between items-center">
              <h2 className="text-3xl font-bold text-gray-900">Order Management</h2>
            </div>

            {/* Order Status Filters */}
            <div className="mb-6 flex gap-3 overflow-x-auto pb-2">
              {[
                { key: 'all', label: 'All Orders', count: orders.length },
                { key: 'pending', label: '⏳ New Orders', count: orders.filter(o => ['pending', 'created', 'paid'].includes(o.status)).length },
                { key: 'hold', label: '⏸️ On Hold', count: orders.filter(o => o.status === 'hold').length },
                { key: 'accepted', label: '✅ Accepted', count: orders.filter(o => o.status === 'accepted').length },
                { key: 'rejected', label: '❌ Rejected', count: orders.filter(o => o.status === 'rejected').length },
              ].map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => setOrderStatusFilter(filter.key)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                    orderStatusFilter === filter.key
                      ? 'bg-orange-500 text-white shadow-md'
                      : 'bg-white text-gray-700 border border-gray-200 hover:border-orange-300'
                  }`}
                >
                  {filter.label} ({filter.count})
                </button>
              ))}
            </div>

            {/* Orders List */}
            {getFilteredOrders().length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-gray-600">
                    {orderStatusFilter === 'all' ? 'No orders yet' : `No ${orderStatusFilter} orders`}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {getFilteredOrders().map((order) => (
                  <Card key={order.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      {/* Order Header */}
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-bold text-gray-900">Order #{order.id}</h3>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getOrderStatusColor(order.status)}`}>
                              {order.status.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">
                            {formatOrderDate(order.created_at)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Total Amount</p>
                          <p className="text-2xl font-bold text-orange-500">₹{(order.amount / 100).toFixed(2)}</p>
                        </div>
                      </div>

                      {/* Order Summary Grid */}
                      <div className="grid md:grid-cols-4 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Customer</p>
                          <p className="font-semibold text-sm">{order.customer_details?.name || 'N/A'}</p>
                          <p className="text-xs text-gray-500">{order.customer_details?.email || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Payment Method</p>
                          <p className="font-semibold text-sm">{getPaymentMethod(order)}</p>
                          <p className="text-xs text-gray-500">
                            {order.payment_status === 'paid' ? '✅ Paid' : '⏳ Pending'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Items</p>
                          <p className="font-semibold text-sm">{getOrderItemsCount(order)} item(s)</p>
                          {order.coupon_code && (
                            <p className="text-xs text-green-600">🎟️ {order.coupon_code}</p>
                          )}
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Subtotal</p>
                          <p className="font-semibold text-sm">₹{((order.subtotal || order.amount) / 100).toFixed(2)}</p>
                          {order.discount_amount > 0 && (
                            <p className="text-xs text-green-600">Discount: -₹{(order.discount_amount / 100).toFixed(2)}</p>
                          )}
                        </div>
                      </div>

                      {/* Shiprocket Info (if accepted) */}
                      {order.status === 'accepted' && order.shiprocket && (
                        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm font-semibold text-blue-900 mb-2">📦 Shipping Details</p>
                          <div className="grid md:grid-cols-3 gap-2 text-sm">
                            <div>
                              <span className="text-gray-600">AWB: </span>
                              <span className="font-mono font-semibold">{order.shiprocket.awb_code || 'Pending'}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Courier: </span>
                              <span className="font-semibold">{order.shiprocket.courier_name || 'TBD'}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Status: </span>
                              <span className="font-semibold">{order.shiprocket.status || 'Processing'}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-2 items-center">
                        {/* View Details Button */}
                        <Button
                          onClick={() => setSelectedOrder(order)}
                          variant="outline"
                          size="sm"
                          className="flex-1"
                        >
                          👁️ View Full Details
                        </Button>

                        {/* Status Actions */}
                        {(order.status === 'pending' || order.status === 'created' || order.status === 'paid') && (
                          <>
                            <Button
                              onClick={() => {
                                if (order.payment_status !== 'paid') {
                                  alert('⚠️ Cannot accept unpaid order.\n\nThis order has payment status: ' + (order.payment_status || 'pending') + '\n\nPlease confirm payment is received before accepting the order.');
                                  return;
                                }
                                updateOrderStatus(order.id, 'accepted');
                              }}
                              className={`${order.payment_status === 'paid' ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-400 hover:bg-gray-500'} text-white`}
                              size="sm"
                              title={order.payment_status !== 'paid' ? 'Payment not confirmed' : 'Accept order and create shipment'}
                            >
                              ✅ Accept
                            </Button>
                            <Button
                              onClick={() => updateOrderStatus(order.id, 'hold')}
                              className="bg-yellow-500 hover:bg-yellow-600 text-white"
                              size="sm"
                            >
                              ⏸️ Hold
                            </Button>
                            <Button
                              onClick={() => updateOrderStatus(order.id, 'rejected')}
                              variant="outline"
                              className="text-red-600 border-red-300 hover:bg-red-50"
                              size="sm"
                            >
                              ❌ Reject
                            </Button>
                          </>
                        )}

                        {order.status === 'accepted' && order.shiprocket && (
                          <>
                            {order.shiprocket.label_url && (
                              <Button
                                onClick={() => viewLabel(order.shiprocket.label_url)}
                                className="flex-1 bg-blue-500 hover:bg-blue-600"
                                size="sm"
                              >
                                🏷️ View Label
                              </Button>
                            )}
                            {order.shiprocket.awb_code && (
                              <Button
                                onClick={() => trackShipment(order.id)}
                                variant="outline"
                                className="flex-1"
                                size="sm"
                              >
                                📍 Track
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Order Details Modal */}
            {selectedOrder && (
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
                onClick={() => setSelectedOrder(null)}
              >
                <div
                  className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-900">
                      Order Details #{selectedOrder.order_id}
                    </h2>
                    <button
                      onClick={() => setSelectedOrder(null)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  <div className="p-6 space-y-6">
                    {/* Order Status & Payment */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Order Status</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div>
                              <p className="text-sm text-gray-600">Status</p>
                              <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold border ${getOrderStatusColor(selectedOrder.status)}`}>
                                {selectedOrder.status.toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Order Date</p>
                              <p className="font-semibold">{formatOrderDate(selectedOrder.created_at)}</p>
                            </div>
                            {selectedOrder.paid_at && (
                              <div>
                                <p className="text-sm text-gray-600">Payment Date</p>
                                <p className="font-semibold">{formatOrderDate(selectedOrder.paid_at)}</p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Payment Information</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div>
                              <p className="text-sm text-gray-600">Payment Method</p>
                              <p className="font-semibold">{getPaymentMethod(selectedOrder)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Payment Status</p>
                              <p className="font-semibold">
                                {selectedOrder.payment_status === 'paid' ? '✅ Paid' : '⏳ Pending'}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Razorpay Order ID</p>
                              <p className="text-xs font-mono break-all">{selectedOrder.razorpay_order_id}</p>
                            </div>
                            {selectedOrder.razorpay_payment_id && (
                              <div>
                                <p className="text-sm text-gray-600">Razorpay Payment ID</p>
                                <p className="text-xs font-mono break-all">{selectedOrder.razorpay_payment_id}</p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Customer Details */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Customer Details</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-600">Name</p>
                            <p className="font-semibold">{selectedOrder.customer_details?.name || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Email</p>
                            <p className="font-semibold">{selectedOrder.customer_details?.email || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Phone</p>
                            <p className="font-semibold">{selectedOrder.customer_details?.phone || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Alternate Phone</p>
                            <p className="font-semibold">{selectedOrder.customer_details?.alternate_phone || 'N/A'}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Shipping Address */}
                    {selectedOrder.customer_details?.address && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Shipping Address</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm text-gray-600">Street Address</p>
                              <p className="font-semibold">{selectedOrder.customer_details.address.street || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">City</p>
                              <p className="font-semibold">{selectedOrder.customer_details.address.city || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">State</p>
                              <p className="font-semibold">{selectedOrder.customer_details.address.state || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">PIN Code</p>
                              <p className="font-semibold">{selectedOrder.customer_details.address.pincode || 'N/A'}</p>
                            </div>
                            <div className="md:col-span-2">
                              <p className="text-sm text-gray-600">Landmark</p>
                              <p className="font-semibold">{selectedOrder.customer_details.address.landmark || 'N/A'}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Order Items */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Order Items</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {selectedOrder.items?.map((item, index) => (
                            <div key={item.product_id || item.id || `item-${index}`} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                              <div className="flex-1">
                                <p className="font-semibold">{item.name || item.product_name || 'Product'}</p>
                                <p className="text-sm text-gray-600">
                                  Quantity: <span className="font-semibold">{item.quantity || item.qty || 1}</span>
                                </p>
                                {item.price && (
                                  <p className="text-sm text-gray-600">
                                    Unit Price: <span className="font-semibold">₹{(item.price / 100).toFixed(2)}</span>
                                  </p>
                                )}
                              </div>
                              {item.price && (
                                <div className="text-right">
                                  <p className="font-bold text-orange-500">
                                    ₹{((item.price * (item.quantity || item.qty || 1)) / 100).toFixed(2)}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Order Summary */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Order Summary</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <p className="text-gray-600">Subtotal</p>
                            <p className="font-semibold">₹{((selectedOrder.subtotal || selectedOrder.amount) / 100).toFixed(2)}</p>
                          </div>
                          {selectedOrder.discount_amount > 0 && (
                            <div className="flex justify-between text-green-600">
                              <p>Discount {selectedOrder.coupon_code && `(${selectedOrder.coupon_code})`}</p>
                              <p className="font-semibold">-₹{(selectedOrder.discount_amount / 100).toFixed(2)}</p>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <p className="text-gray-600">Delivery Charge</p>
                            <p className="font-semibold">
                              {(selectedOrder.delivery_charge || 0) === 0 ? 'FREE' : `₹${(selectedOrder.delivery_charge / 100).toFixed(2)}`}
                            </p>
                          </div>
                          <div className="flex justify-between pt-3 border-t border-gray-200">
                            <p className="text-lg font-bold">Total Amount</p>
                            <p className="text-2xl font-bold text-orange-500">₹{(selectedOrder.amount / 100).toFixed(2)}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Shiprocket Details */}
                    {selectedOrder.shiprocket && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Shipping Information</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm text-gray-600">AWB Code</p>
                              <p className="font-mono font-semibold">{selectedOrder.shiprocket.awb_code || 'Pending'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Courier</p>
                              <p className="font-semibold">{selectedOrder.shiprocket.courier_name || 'TBD'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Shipment ID</p>
                              <p className="font-mono text-sm">{selectedOrder.shiprocket.shipment_id || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Order ID</p>
                              <p className="font-mono text-sm">{selectedOrder.shiprocket.order_id || 'N/A'}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Coupons Tab */}
        {activeTab === 'coupons' && (
          <>
            <div className="mb-6 flex justify-between items-center">
              <h2 className="text-3xl font-bold text-gray-900">Coupon Management</h2>
              <Button
                onClick={() => setShowCouponForm(!showCouponForm)}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Coupon
              </Button>
            </div>

            {showCouponForm && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Create New Coupon</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCreateCoupon} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Coupon Code *</Label>
                        <Input
                          value={newCoupon.code}
                          onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                          placeholder="e.g., SAVE20"
                          required
                        />
                      </div>
                      <div>
                        <Label>Discount Type *</Label>
                        <select
                          value={newCoupon.discount_type}
                          onChange={(e) => setNewCoupon({ ...newCoupon, discount_type: e.target.value })}
                          className="w-full border rounded px-3 py-2"
                          required
                        >
                          <option value="percentage">Percentage (%)</option>
                          <option value="fixed">Fixed (₹)</option>
                        </select>
                      </div>
                      <div>
                        <Label>Discount Value *</Label>
                        <Input
                          type="number"
                          value={newCoupon.discount_value}
                          onChange={(e) => setNewCoupon({ ...newCoupon, discount_value: e.target.value })}
                          placeholder={newCoupon.discount_type === 'percentage' ? 'e.g., 20' : 'e.g., 100'}
                          required
                        />
                      </div>
                      <div>
                        <Label>Min Order Value (₹)</Label>
                        <Input
                          type="number"
                          value={newCoupon.min_order_value}
                          onChange={(e) => setNewCoupon({ ...newCoupon, min_order_value: e.target.value })}
                          placeholder="e.g., 500"
                        />
                      </div>
                      <div>
                        <Label>Expiry Date *</Label>
                        <Input
                          type="datetime-local"
                          value={newCoupon.expiry_date}
                          onChange={(e) => setNewCoupon({ ...newCoupon, expiry_date: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <Button type="submit" className="bg-orange-500 hover:bg-orange-600">
                      Create Coupon
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {coupons.map((coupon) => (
                <Card key={coupon.id}>
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="text-2xl font-bold text-orange-500">{coupon.code}</p>
                        <p className="text-sm text-gray-600">
                          {coupon.discount_type === 'percentage' 
                            ? `${coupon.discount_value}% OFF` 
                            : `₹${coupon.discount_value} OFF`}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        coupon.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {coupon.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <p>Min Order: ₹{coupon.min_order_value || 0}</p>
                      <p>Used: {coupon.times_used || 0} times</p>
                      <p>Expires: {new Date(coupon.expiry_date).toLocaleDateString()}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* AI Image Generator Tab */}
        {activeTab === 'ai-generator' && <AIImageGenerator />}
      </div>
    </div>
  );
};

export default AdminDashboardNew;
